import Box from "@mui/material/Box";
import {alpha} from "@mui/material/styles";
import type {ForwardedRef, PointerEvent as ReactPointerEvent, ReactNode} from "react";
import {forwardRef, useCallback, useEffect, useLayoutEffect, useRef, useState} from "react";

const THUMB_WIDTH = 6;
const THUMB_INSET = 2;
const THUMB_MIN_HEIGHT = 24;
/** How long the thumb stays after the last scroll event, so it does not flicker between wheel ticks. */
const SCROLL_LINGER_MS = 800;

interface OverlayScrollAreaProps {
    maxHeight: number;
    onScroll?: () => void;
    children: ReactNode;
}

/**
 * A scroll container whose scrollbar floats over the content.
 *
 * A native scrollbar takes its width from the rows on platforms that show one, and the list would
 * lay out differently on a Mac with a mouse than with a trackpad. The native bar is hidden and a
 * thin thumb is drawn on top instead, shown only while it is useful: hovering or scrolling.
 */
export const OverlayScrollArea = forwardRef(function OverlayScrollArea(
    {maxHeight, onScroll, children}: OverlayScrollAreaProps,
    ref: ForwardedRef<HTMLDivElement>,
) {
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const thumbRef = useRef<HTMLDivElement | null>(null);
    const lingerTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const drag = useRef<{pointerId: number; startY: number; startScrollTop: number} | null>(null);

    const [overflows, setOverflows] = useState(false);
    const [hovering, setHovering] = useState(false);
    const [scrolling, setScrolling] = useState(false);
    const [dragging, setDragging] = useState(false);

    const setScrollElement = useCallback((element: HTMLDivElement | null) => {
        scrollRef.current = element;
        if (typeof ref === "function") ref(element);
        else if (ref) ref.current = element;
    }, [ref]);

    /*
        Position and size are written straight onto the thumb: they change on every scroll frame,
        and a React render per frame is work the list does not need.
     */
    const placeThumb = useCallback(() => {
        const element = scrollRef.current;
        if (!element) return;

        const {scrollHeight, clientHeight, scrollTop} = element;
        const nextOverflows = scrollHeight > clientHeight + 1;
        setOverflows(nextOverflows);

        const thumb = thumbRef.current;
        if (!thumb || !nextOverflows) return;

        const track = clientHeight - THUMB_INSET * 2;
        const height = Math.max(THUMB_MIN_HEIGHT, track * clientHeight / scrollHeight);
        const top = THUMB_INSET + (scrollTop / (scrollHeight - clientHeight)) * (track - height);

        thumb.style.height = `${height}px`;
        thumb.style.transform = `translateY(${top}px)`;
    }, []);

    // The content changes size with every query; measuring after each render keeps the thumb honest.
    useLayoutEffect(placeThumb);

    useEffect(() => {
        const element = scrollRef.current;
        if (!element || typeof ResizeObserver === "undefined") return;

        const observer = new ResizeObserver(() => placeThumb());
        observer.observe(element);
        if (element.firstElementChild) observer.observe(element.firstElementChild);

        return () => observer.disconnect();
    }, [placeThumb]);

    useEffect(() => () => {
        if (lingerTimer.current) clearTimeout(lingerTimer.current);
    }, []);

    const handleScroll = () => {
        placeThumb();
        setScrolling(true);
        if (lingerTimer.current) clearTimeout(lingerTimer.current);
        lingerTimer.current = setTimeout(() => setScrolling(false), SCROLL_LINGER_MS);
        onScroll?.();
    };

    const startDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
        const element = scrollRef.current;
        if (!element) return;

        // The panel never takes focus: grabbing the thumb must not blur the editor.
        event.preventDefault();
        event.currentTarget.setPointerCapture?.(event.pointerId);
        drag.current = {pointerId: event.pointerId, startY: event.clientY, startScrollTop: element.scrollTop};
        setDragging(true);
    };

    const moveDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
        const element = scrollRef.current;
        const thumb = thumbRef.current;
        const current = drag.current;
        if (!element || !thumb || !current || current.pointerId !== event.pointerId) return;

        const {scrollHeight, clientHeight} = element;
        const travel = clientHeight - THUMB_INSET * 2 - thumb.offsetHeight;
        if (travel <= 0) return;

        element.scrollTop = current.startScrollTop
            + (event.clientY - current.startY) * (scrollHeight - clientHeight) / travel;
    };

    const endDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
        if (drag.current?.pointerId !== event.pointerId) return;

        event.currentTarget.releasePointerCapture?.(event.pointerId);
        drag.current = null;
        setDragging(false);
    };

    return (
        <Box
            onMouseEnter={() => setHovering(true)}
            onMouseLeave={() => setHovering(false)}
            sx={{position: "relative"}}
        >
            <Box
                ref={setScrollElement}
                onScroll={handleScroll}
                sx={{
                    maxHeight,
                    overflowY: "auto",
                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": {display: "none"},
                }}
            >
                {children}
            </Box>

            {overflows && (
                <Box
                    ref={thumbRef}
                    aria-hidden
                    onPointerDown={startDrag}
                    onPointerMove={moveDrag}
                    onPointerUp={endDrag}
                    onPointerCancel={endDrag}
                    onMouseDown={event => event.preventDefault()}
                    sx={{
                        position: "absolute",
                        top: 0,
                        right: THUMB_INSET,
                        width: THUMB_WIDTH,
                        borderRadius: `${THUMB_WIDTH / 2}px`,
                        bgcolor: theme => alpha(theme.palette.text.primary, 0.25),
                        opacity: hovering || scrolling || dragging ? 1 : 0,
                        transition: "opacity 160ms ease-out",
                        cursor: "default",
                        touchAction: "none",
                    }}
                />
            )}
        </Box>
    );
});

export default OverlayScrollArea;
